/**
 * useMultiplayer Hook - Firebase multiplayer synchronization
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { 
  collection, 
  doc, 
  setDoc, 
  getDoc, 
  updateDoc, 
  onSnapshot,
  serverTimestamp,
  deleteDoc,
} from 'firebase/firestore';
import { database } from '../../../database/firebase';
import { createInitialState } from '../engine/gameLogic';

const ROOMS_COLLECTION = 'shiftTicTacToe_rooms';

function generateRoomCode() {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
  let code = '';
  for (let i = 0; i < 4; i++) {
    code += chars[Math.floor(Math.random() * chars.length)];
  }
  return code;
}

function generatePlayerId() {
  return `player_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
}

export function useMultiplayer() {
  const [roomCode, setRoomCode] = useState(null);
  const [playerId] = useState(() => generatePlayerId());
  const [isHost, setIsHost] = useState(false);
  const [roomData, setRoomData] = useState(null);
  const [error, setError] = useState(null);
  const [connectionStatus, setConnectionStatus] = useState('disconnected');
  
  const unsubscribeRef = useRef(null);
  const roomCodeRef = useRef(null);

  useEffect(() => {
    roomCodeRef.current = roomCode;
  }, [roomCode]);

  useEffect(() => {
    return () => {
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
      }
    };
  }, []);

  const subscribeToRoom = useCallback((code) => {
    if (unsubscribeRef.current) {
      unsubscribeRef.current();
    }

    const roomRef = doc(database, ROOMS_COLLECTION, code);
    
    unsubscribeRef.current = onSnapshot(
      roomRef,
      (snapshot) => {
        if (snapshot.exists()) {
          const data = snapshot.data();
          setRoomData(data);
          setConnectionStatus('connected');
          setError(null);
        } else {
          setRoomData(null);
          setError('Room no longer exists');
          setConnectionStatus('disconnected');
        }
      },
      (err) => {
        console.error('Firestore subscription error:', err);
        setError('Connection lost');
        setConnectionStatus('disconnected');
      }
    );
  }, []);

  const createRoom = useCallback(async (settings = {}) => {
    setError(null);
    setConnectionStatus('connecting');
    
    try {
      let code = generateRoomCode();
      let attempts = 0;
      
      while (attempts < 10) {
        const roomRef = doc(database, ROOMS_COLLECTION, code);
        const existing = await getDoc(roomRef);
        
        if (!existing.exists()) {
          break;
        }
        
        code = generateRoomCode();
        attempts++;
      }
      
      const initialGameState = createInitialState({
        maxPieces: settings.maxPieces || 4,
        allowDiagonal: settings.allowDiagonal !== false,
        startingPlayer: 'X',
      });

      const roomData = {
        hostId: playerId,
        guestId: null,
        gameState: initialGameState,
        status: 'waiting',
        settings: {
          maxPieces: settings.maxPieces || 4,
          allowDiagonal: settings.allowDiagonal !== false,
        },
        createdAt: serverTimestamp(),
        lastActivity: serverTimestamp(),
        rematchRequested: null,
      };

      await setDoc(doc(database, ROOMS_COLLECTION, code), roomData);
      
      setRoomCode(code);
      setIsHost(true);
      subscribeToRoom(code);
      
      return { success: true, roomCode: code };
    } catch (err) {
      console.error('Create room error:', err);
      setError('Failed to create room');
      setConnectionStatus('disconnected');
      return { success: false, error: 'Failed to create room' };
    }
  }, [playerId, subscribeToRoom]);

  const joinRoom = useCallback(async (code) => {
    setError(null);
    setConnectionStatus('connecting');
    
    try {
      const normalizedCode = code.toUpperCase().trim();
      const roomRef = doc(database, ROOMS_COLLECTION, normalizedCode);
      const snapshot = await getDoc(roomRef);
      
      if (!snapshot.exists()) {
        setError('Room not found');
        setConnectionStatus('disconnected');
        return { success: false, error: 'Room not found' };
      }
      
      const data = snapshot.data();
      
      if (data.guestId && data.guestId !== playerId) {
        setError('Room is full');
        setConnectionStatus('disconnected');
        return { success: false, error: 'Room is full' };
      }
      
      if (data.hostId === playerId) {
        setRoomCode(normalizedCode);
        setIsHost(true);
        subscribeToRoom(normalizedCode);
        return { success: true, roomCode: normalizedCode };
      }

      await updateDoc(roomRef, {
        guestId: playerId,
        status: 'playing',
        lastActivity: serverTimestamp(),
      });
      
      setRoomCode(normalizedCode);
      setIsHost(false);
      subscribeToRoom(normalizedCode);
      
      return { success: true, roomCode: normalizedCode };
    } catch (err) {
      console.error('Join room error:', err);
      setError('Failed to join room');
      setConnectionStatus('disconnected');
      return { success: false, error: 'Failed to join room' };
    }
  }, [playerId, subscribeToRoom]);

  const updateGameState = useCallback(async (newGameState) => {
    if (!roomCodeRef.current) return;
    
    try {
      const roomRef = doc(database, ROOMS_COLLECTION, roomCodeRef.current);
      await updateDoc(roomRef, {
        gameState: newGameState,
        lastActivity: serverTimestamp(),
      });
    } catch (err) {
      console.error('Update game state error:', err);
      setError('Failed to sync move');
    }
  }, []);

  const requestRematch = useCallback(async () => {
    if (!roomCodeRef.current) return;
    
    try {
      const roomRef = doc(database, ROOMS_COLLECTION, roomCodeRef.current);
      await updateDoc(roomRef, {
        rematchRequested: playerId,
        lastActivity: serverTimestamp(),
      });
    } catch (err) {
      console.error('Request rematch error:', err);
    }
  }, [playerId]);

  const acceptRematch = useCallback(async (settings = {}) => {
    if (!roomCodeRef.current) return;
    
    try {
      const newGameState = createInitialState({
        maxPieces: settings.maxPieces || 4,
        allowDiagonal: settings.allowDiagonal !== false,
        startingPlayer: 'X',
      });

      const roomRef = doc(database, ROOMS_COLLECTION, roomCodeRef.current);
      await updateDoc(roomRef, {
        gameState: newGameState,
        status: 'playing',
        rematchRequested: null,
        lastActivity: serverTimestamp(),
      });
    } catch (err) {
      console.error('Accept rematch error:', err);
    }
  }, []);

  const leaveRoom = useCallback(async () => {
    if (!roomCodeRef.current) return;
    
    try {
      const roomRef = doc(database, ROOMS_COLLECTION, roomCodeRef.current);
      
      if (isHost) {
        await deleteDoc(roomRef);
      } else {
        await updateDoc(roomRef, {
          guestId: null,
          status: 'waiting',
          lastActivity: serverTimestamp(),
        });
      }
    } catch (err) {
      console.error('Leave room error:', err);
    } finally {
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
        unsubscribeRef.current = null;
      }
      setRoomCode(null);
      setRoomData(null);
      setIsHost(false);
      setConnectionStatus('disconnected');
    }
  }, [isHost]);

  const opponentJoined = roomData?.guestId !== null && roomData?.guestId !== undefined;
  const gameState = roomData?.gameState || null;
  const playerRole = isHost ? 'X' : 'O';
  const isMyTurn = gameState?.currentPlayer === playerRole;
  const rematchRequested = roomData?.rematchRequested;
  const opponentRequestedRematch = rematchRequested && rematchRequested !== playerId;

  return {
    roomCode,
    playerId,
    isHost,
    playerRole,
    gameState,
    opponentJoined,
    isMyTurn,
    connectionStatus,
    error,
    rematchRequested,
    opponentRequestedRematch,
    createRoom,
    joinRoom,
    updateGameState,
    requestRematch,
    acceptRematch,
    leaveRoom,
  };
}

export default useMultiplayer;
